import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { get } from '../../helpers/api';
import { useCopyToClipboard, useFormatters, useToast } from '../../hooks';
import { Button, Divider, IconButton, Paper, Stack, Typography } from '../../ui/core';
import { ContentCopyIcon } from '../../ui/icons';
import EventLogTimeline from '../../components/widgets/EventLog/EventLogTimeline';
import { useEventLogFilters } from '../../components/widgets/EventLog/eventLogFilters';
import { RideStatusChip, RideFare } from '../../components/widgets/Trips/RidersList';
import { WaypointSummary } from './WaypointSummary';
import RideLiveState from './RideLiveState';

/**
 * The ride's detail column: who and where, the live state the rider's app is
 * on, and the event log. `live` is the caller's useRideLive result — the caller
 * owns the stream because the map beside this panel draws from it too.
 */
const RidePanel = ({ ride, live }) => {
  const f = useFormatters();
  const c = useCopyToClipboard();
  const t = useToast();
  const [events, setEvents] = useState(null);
  const [eventsError, setEventsError] = useState(false);
  const [eventsRefetching, setEventsRefetching] = useState(false);
  const filters = useEventLogFilters();
  const { queryString } = filters;

  // Clear the previous ride's events/error so neither flashes for a frame
  // after switching rides.
  useEffect(() => {
    setEvents(null);
    setEventsError(false);
  }, [ride.id]);

  // Fetch (and refetch) events whenever the server-side filter selection
  // changes. We don't null `events` here: on a filter refetch the prior rows
  // stay visible and `eventsRefetching` drives a progress indicator; the
  // skeleton only shows on the initial load.
  useEffect(() => {
    let cancelled = false;

    const fetchEvents = async () => {
      setEventsRefetching(true);
      setEventsError(false);
      try {
        const response = await get(`/admin/ride/${ride.id}/events${queryString}`);
        if (!cancelled) setEvents(response.data);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching ride events:', err);
        setEventsError(true);
        t.error('There was an error loading the event log');
      } finally {
        if (!cancelled) setEventsRefetching(false);
      }
    };

    fetchEvents();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ride.id, queryString]);

  const copyIdToClipboard = () => {
    c.copy(ride.id);
    t.success('Ride id copied to clipboard');
  };

  const departureDateIso = ride.departureTime
    ? f.date.toLocaleString(ride.departureTime, f.date.DateFormats.DATE_ISO)
    : null;
  const tripUrl =
    ride.tripId && departureDateIso
      ? `/trips/daily?date=${departureDateIso}&tripId=${ride.tripId}&fullScreen=true`
      : null;
  const riderName = ride.rider ? `${ride.rider.firstName || ''} ${ride.rider.lastName || ''}`.trim() : null;

  return (
    <>
      <Paper variant="outlined" sx={{ padding: '12px 16px' }}>
        <Stack spacing="12px">
          {/* The id is the long thing on this page and the least read, so it
              gets one line and an ellipsis rather than three lines of h5. */}
          <Stack direction="row" spacing="8px" alignItems="center">
            <Typography color="text.secondary" variant="body2">
              Ride
            </Typography>
            <Typography
              variant="body2"
              title={ride.id}
              sx={{
                flex: 1,
                minWidth: 0,
                fontFamily: 'monospace',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {ride.id}
            </Typography>
            <IconButton size="small" onClick={copyIdToClipboard}>
              <ContentCopyIcon sx={{ fontSize: '16px' }} />
            </IconButton>
            <RideStatusChip status={ride.status} />
          </Stack>

          <Stack direction="row" spacing="8px" alignItems="center" flexWrap="wrap" useFlexGap>
            {tripUrl ? (
              <Button size="small" component={Link} to={tripUrl}>
                View trip
              </Button>
            ) : (
              <Typography color="text.secondary" variant="body2">
                No trip attached
              </Typography>
            )}
            {ride.standbyRideId ? (
              <Button size="small" component={Link} to={`/rides/${ride.standbyRideId}`}>
                Originated as standby request
              </Button>
            ) : null}
          </Stack>

          <Divider />

          {/* Label left, value right: the pairs used to sit inline and wrapped
              mid-pair, which read as four stray words. */}
          <Stack spacing="6px">
            <Stack direction="row" justifyContent="space-between" spacing="8px">
              <Typography color="text.secondary" variant="body2">
                Rider
              </Typography>
              {ride.rider ? (
                <Button
                  component={Link}
                  to={`/users/${ride.rider.id}`}
                  sx={{ padding: 0, minWidth: 0, textTransform: 'none' }}
                >
                  {riderName || ride.rider.id}
                </Button>
              ) : (
                <Typography variant="body2">—</Typography>
              )}
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing="8px">
              <Typography color="text.secondary" variant="body2">
                Seats
              </Typography>
              <Typography variant="body2">{ride.seatCount}</Typography>
            </Stack>
            {ride.fare ? (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <RideFare ride={ride} label="Fare" />
              </Stack>
            ) : (
              <Stack direction="row" justifyContent="space-between" spacing="8px">
                <Typography color="text.secondary" variant="body2">
                  Fare
                </Typography>
                <Typography variant="body2">—</Typography>
              </Stack>
            )}
          </Stack>

          <Divider />

          {/* Stacked, not a left-to-right row with an arrow: two addresses and
              two timestamps do not fit across 420px. */}
          <Stack spacing="10px">
            <WaypointSummary stacked label="Pick-up" waypoint={ride.pickUp} time={ride.departureTime} />
            <WaypointSummary stacked label="Drop-off" waypoint={ride.dropOff} time={ride.arrivalTime} />
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ padding: '12px 16px' }}>
        <RideLiveState state={live.state} rider={live.rider} feed={live.feed} connected={live.connected} />
      </Paper>

      <Paper variant="outlined" sx={{ padding: '12px 16px 24px 16px' }}>
        <Typography variant="subtitle1" sx={{ marginTop: '8px' }}>
          Event Log
        </Typography>
        {eventsError ? (
          <Typography variant="body2" color="error.main" sx={{ padding: '8px 0' }}>
            There was an error loading the event log.
          </Typography>
        ) : (
          <EventLogTimeline events={events} loading={events === null} refetching={eventsRefetching} filters={filters} />
        )}
      </Paper>
    </>
  );
};

export default RidePanel;
